// src/features/collaboration/yjsUtils.ts
import * as Y from 'yjs';
import { initTiming } from '../analytics/initTiming';
import { YjsKeyManager } from './yjsKeyManager';

/**
 * 安全なY.Doc復元関数（破損データ検出時は新規Doc生成）
 */
export const safeRestoreYjsFromFirestoreData = (
  firestoreData: { editData?: { ydocBytes?: any; title?: string; version?: number }; yjsState?: any; tiptapJSON?: any; title?: string; slug?: string },
  boardId?: string
): Y.Doc => {
  try {
    // 新DB構造のeditData.ydocBytesを優先、レガシーのyjsStateも対応
    const yjsStateSource = firestoreData.editData?.ydocBytes || firestoreData.yjsState;

    if (yjsStateSource) {
      // Firestore Bytes型からUint8Arrayに変換
      let yjsStateArray: Uint8Array;

      if (yjsStateSource instanceof Uint8Array) {
        yjsStateArray = yjsStateSource;
      } else if (yjsStateSource.toUint8Array && typeof yjsStateSource.toUint8Array === 'function') {
        try {
          yjsStateArray = yjsStateSource.toUint8Array();
        } catch {
          return new Y.Doc();
        }
      } else {
        yjsStateArray = decodeBase64ToYjs(yjsStateSource);
      }

      // データ構造の事前チェック
      if (!isValidYjsData(yjsStateArray)) {
        return new Y.Doc();
      }

      // 新しいY.Docを作成して復元を試行
      const newDoc = new Y.Doc();
      
      try {
        const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const applyUpdateV2 = (Y as any).applyUpdateV2 as undefined | ((doc: Y.Doc, update: Uint8Array) => void);
        let appliedWith: 'v2' | 'v1' = 'v1';
        let v2Error: string | null = null;
        if (applyUpdateV2) {
          try {
            applyUpdateV2(newDoc, yjsStateArray);
            appliedWith = 'v2';
          } catch (e: any) {
            v2Error = e?.message ? String(e.message) : String(e);
            // fallback to v1
            Y.applyUpdate(newDoc, yjsStateArray);
            appliedWith = 'v1';
          }
        } else {
          Y.applyUpdate(newDoc, yjsStateArray);
        }
        const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

        try {
          initTiming.mark('FS_APPLY', {
            docSize: yjsStateArray.length,
            extra: { applyMs: Math.max(0, t1 - t0), format: appliedWith, v2Error: v2Error || undefined }
          });
        } catch (_) {}

        // 復元後の整合性チェック
        if (!validateRestoredYdoc(newDoc)) {
          return new Y.Doc();
        }

        // 復元後にcontentフィールドが確実に存在することを保証
        YjsKeyManager.ensureContentXmlFragment(newDoc);

        return newDoc;
      } catch (applyError) {
        // 破損データの場合はFirestoreからデータをクリア
        if (applyError instanceof Error && (
          applyError.message.includes('Integer out of Range') ||
          applyError.message.includes('Unexpected case') ||
          applyError.message.includes('Invalid update') ||
          applyError.message.includes('Invalid state') ||
          applyError.message.includes('Decoding error')
        )) {
          // グローバルに破損データクリア通知を送信
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('corrupted-data-detected', {
              detail: { 
                boardId, 
                error: applyError.message,
                dataLength: yjsStateArray.length,
                timestamp: new Date().toISOString()
              }
            }));
          }
        }
        
        // 破損データの場合は新規Docを返す
        return new Y.Doc();
      }
    }

    // データが存在しない場合は新規Docを返す
    return new Y.Doc();
  } catch {
    return new Y.Doc();
  }
};

/**
 * Y.DocからFirestore保存用データに変換
 */
export const convertYjsToFirestoreData = (ydoc: Y.Doc): { yjsState: Uint8Array } => {
  // boardId の必須チェック（データ汚染防止）
  const boardId = (ydoc as any).boardId;

  // Y.Docのshareキー一覧を取得
  const shareKeys = Array.from(ydoc.share.keys());

  // 各フィールドの詳細情報を調査
  const fieldDetails: Record<string, any> = {};
  shareKeys.forEach(key => {
    try {
      const field = ydoc.share.get(key);
      let fieldInfo: any = {
        key,
        exists: !!field,
        type: field?.constructor?.name || typeof field,
        hasToString: field ? typeof field.toString === 'function' : false,
        hasLength: field ? 'length' in field : false,
        hasSize: field ? 'size' in field : false,
        hasKeys: field ? typeof (field as any).keys === 'function' : false
      };

      if (!field) {
        fieldInfo.error = 'Field is null or undefined';
        fieldDetails[key] = fieldInfo;
        return;
      }

      // lengthがある場合は取得
      if (fieldInfo.hasLength) {
        try {
          fieldInfo.length = (field as any).length;
        } catch (e) {
          fieldInfo.lengthError = e instanceof Error ? e.message : String(e);
        }
      }

      // sizeがある場合は取得
      if (fieldInfo.hasSize) {
        try {
          fieldInfo.size = (field as any).size;
        } catch (e) {
          fieldInfo.sizeError = e instanceof Error ? e.message : String(e);
        }
      }

      // toStringが利用可能な場合は内容の一部を取得
      if (fieldInfo.hasToString && field) {
        try {
          const content = field.toString();
          fieldInfo.contentLength = content.length;
          fieldInfo.contentPreview = content.substring(0, 200) + (content.length > 200 ? '...' : '');
          fieldInfo.hasContent = content.trim().length > 0;
        } catch (e) {
          fieldInfo.contentError = e instanceof Error ? e.message : String(e);
        }
      }

      // Map型の場合はキー一覧を取得
      if (fieldInfo.hasKeys && field) {
        try {
          const mapKeys = Array.from((field as any).keys());
          fieldInfo.mapKeys = mapKeys;
          fieldInfo.mapSize = mapKeys.length;
        } catch (e) {
          fieldInfo.mapKeysError = e instanceof Error ? e.message : String(e);
        }
      }

      fieldDetails[key] = fieldInfo;
    } catch (error) {
      fieldDetails[key] = { 
        key, 
        error: error instanceof Error ? error.message : String(error) 
      };
    }
  });

  // 🔍 各フィールドの詳細内容を個別に出力
  shareKeys.forEach(key => {
    const detail = fieldDetails[key];

    // contentフィールドとmeta2フィールドの詳細比較
    if (key === 'content') {
      try {
        const field = ydoc.share.get(key);
        if (field) {}
      } catch {}
    }

    if (key.startsWith('meta2_')) {
      try {
        const field = ydoc.share.get(key);
        if (field) {
          // YMapの場合、内容を詳細に調査（ログは出さない）
          if (field.constructor.name === 'YMap') {
            const mapEntries: Record<string, any> = {};
            (field as any).forEach((value: any, key: string) => {
              try {
                if (value && typeof value.toString === 'function') {
                  mapEntries[key] = {
                    type: value.constructor?.name || typeof value,
                    value: value.toString(),
                    raw: value
                  };
                } else {
                  mapEntries[key] = {
                    type: typeof value,
                    value: value,
                    raw: value
                  };
                }
              } catch (e) {
                mapEntries[key] = { error: e instanceof Error ? e.message : String(e) };
              }
            });

            // TipTapコンテンツ混入チェックは行うが、結果はログに出さない
            const suspiciousKeys = Object.keys(mapEntries).filter(k => 
              k.includes('content') || 
              k.includes('json') || 
              k.includes('doc') ||
              k.includes('prosemirror') ||
              mapEntries[k].value?.includes('type') ||
              mapEntries[k].value?.includes('content')
            );

            if (suspiciousKeys.length > 0) {
              suspiciousKeys.forEach(suspiciousKey => {});
            }
          }
        }
      } catch {}
    }
  });

  // コンテンツフィールドの特定調査
  const contentFields = shareKeys.filter(key => 
    key.includes('content') || 
    key === 'default' ||
    key.startsWith('collaboration-content') ||
    fieldDetails[key]?.hasContent
  );

  // 実際にコンテンツが入っているフィールドを特定
  const fieldsWithContent = shareKeys.filter(key => {
    const detail = fieldDetails[key];
    return detail?.hasContent || (detail?.length && detail.length > 0);
  });

  if (!boardId || boardId === 'unknown' || boardId === 'new') {
    throw new Error('Valid boardId is required for Y.Doc save operations');
  }

  // ⚠️ ここでは YjsKeyManager による構造変更は行わず、
  // 純粋な Y.Doc の状態をそのままエンコードする。

  let yjsState: Uint8Array;
  try {
    // 通常パス: V2 でエンコード
    yjsState = Y.encodeStateAsUpdateV2(ydoc);
  } catch (encodeError) {
    // フォールバック: V1 で再試行
    try {
      yjsState = Y.encodeStateAsUpdate(ydoc);
    } catch {
      // 元のエラーを再スロー
      throw encodeError;
    }
  }

  return { yjsState };
};

/**
 * Firestoreから取得したデータをY.Docに復元（新DB構造対応）
 */
export const restoreYjsFromFirestoreData = (
  ydoc: Y.Doc, 
  firestoreData: { editData?: { ydocBytes?: any; title?: string; version?: number }; yjsState?: any; tiptapJSON?: any; title?: string; slug?: string },
  boardId?: string
): boolean => {
  try {
    // 新DB構造のeditData.ydocBytesを優先、レガシーのyjsStateも対応
    const yjsStateSource = firestoreData.editData?.ydocBytes || firestoreData.yjsState;

    if (yjsStateSource) {
      // Firestore Bytes型からUint8Arrayに変換
      let yjsStateArray: Uint8Array;

      if (yjsStateSource instanceof Uint8Array) {
        // 直接Uint8Arrayの場合
        yjsStateArray = yjsStateSource;
      } else if (yjsStateSource.toUint8Array && typeof yjsStateSource.toUint8Array === 'function') {
        // Firestore Bytes型の場合
        try {
          yjsStateArray = yjsStateSource.toUint8Array();
        } catch {
          return false;
        }
      } else {
        yjsStateArray = decodeBase64ToYjs(yjsStateSource);
      }

      // データ整合性チェック
      if (!yjsStateArray || yjsStateArray.length === 0) {
        return false;
      }

      // データ構造の事前チェック
      if (!isValidYjsData(yjsStateArray)) {
        return false;
      }

      try {
        const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const applyUpdateV2 = (Y as any).applyUpdateV2 as undefined | ((doc: Y.Doc, update: Uint8Array) => void);
        let appliedWith: 'v2' | 'v1' = 'v1';
        let v2Error: string | null = null;
        let v1Error: string | null = null;

        if (applyUpdateV2) {
          try {
            applyUpdateV2(ydoc, yjsStateArray);
            appliedWith = 'v2';
          } catch (e: any) {
            v2Error = e?.message ? String(e.message) : String(e);
            try {
              Y.applyUpdate(ydoc, yjsStateArray);
              appliedWith = 'v1';
            } catch (e2: any) {
              v1Error = e2?.message ? String(e2.message) : String(e2);
              throw e2;
            }
          }
        } else {
          try {
            Y.applyUpdate(ydoc, yjsStateArray);
            appliedWith = 'v1';
          } catch (e2: any) {
            v1Error = e2?.message ? String(e2.message) : String(e2);
            throw e2;
          }
        }
        const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

        try {
          initTiming.mark('FS_APPLY', {
            docSize: yjsStateArray.length,
            extra: { applyMs: Math.max(0, t1 - t0), format: appliedWith, v2Error: v2Error || undefined, v1Error: v1Error || undefined }
          });
        } catch (_) {}

        // 復元後にcontentフィールドが確実に存在することを保証
        YjsKeyManager.ensureContentXmlFragment(ydoc);

        // 復元後の整合性チェック
        if (!validateRestoredYdoc(ydoc)) {
          console.warn('⚠️ 復元後のY.Doc整合性チェック失敗 - 新規ドキュメントとして初期化:', {
            boardId,
            dataLength: yjsStateArray.length
          });
          return false;
        }
        
      } catch (applyError) {
        // 破損データの場合はFirestoreからデータをクリア
        // NOTE: V2/V1 の適用を誤って「破損」と誤判定しないよう、ここでは applyError のみで判定する
        if (applyError instanceof Error && (
          applyError.message.includes('Integer out of Range') ||
          applyError.message.includes('Unexpected case') ||
          applyError.message.includes('Invalid update') ||
          applyError.message.includes('Invalid state') ||
          applyError.message.includes('Decoding error')
        )) {
          // グローバルに破損データクリア通知を送信
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('corrupted-data-detected', {
              detail: { 
                boardId, 
                error: applyError.message,
                dataLength: yjsStateArray.length,
                timestamp: new Date().toISOString()
              }
            }));
          }
          
          // 復元失敗として扱い、新しいドキュメントとして開始
          return false;
        }
        
        // その他のエラーは再スロー
        throw applyError;
      }

      // 【復元パス2-補強】Firestore applyUpdate 直後の型固定実行
      if (boardId) {
        try {
          if (boardId !== 'unknown' && boardId !== 'new') {
            YjsKeyManager.enforceStrictTyping(ydoc, boardId, { log: false });
          }
        } catch {}
      }

      return true;
    }

    // yjsStateSource が存在しない場合は単に false を返す
    return false;
  } catch {
    return false;
  }
};

export const TABLE_NODE_TYPES = ['table', 'tableRow', 'tableCell', 'tableHeader'];

/**
 * table / tableRow / tableCell / tableHeader を Y.XmlElement に変換する共通処理。
 *
 * 子要素は呼び出し側の toXmlNodes に委譲するため、この関数を各 toXmlNodes の分岐から
 * 呼ぶだけで表全体が再帰的に構築される。
 *
 * colspan / rowspan / colwidth は文字列化しない。prosemirror-tables が数値・配列として
 * 演算に使うため、y-prosemirror が書き込むのと同じ生の型で保持する必要がある。
 */
export const buildTableXmlElement = (
  node: any,
  toXmlNodes: (nodes: any[]) => (Y.XmlElement | Y.XmlText)[]
): Y.XmlElement | null => {
  const type = node?.type;
  if (!TABLE_NODE_TYPES.includes(type)) return null;

  const el = new (Y as any).XmlElement(type);
  const attrs = node?.attrs || {};

  if (type === 'tableCell' || type === 'tableHeader') {
    try {
      const colspan = Number(attrs.colspan);
      const rowspan = Number(attrs.rowspan);
      el.setAttribute('colspan', Number.isFinite(colspan) && colspan > 0 ? colspan : 1);
      el.setAttribute('rowspan', Number.isFinite(rowspan) && rowspan > 0 ? rowspan : 1);
      if (Array.isArray(attrs.colwidth) && attrs.colwidth.length > 0) {
        el.setAttribute('colwidth', attrs.colwidth);
      }
    } catch {}
  }

  if (Array.isArray(node.content)) {
    for (const child of toXmlNodes(node.content)) {
      try { el.insert(el.length, [child as any]); } catch {}
    }
  }

  return el;
};

/**
 * TipTapのcontentJSONをY.DocのXmlFragment('content')にシードする（簡易対応）
 * 対応ノード: doc, paragraph, heading(level), text, 表
 * 既存contentが空の場合の初期シード用途を想定
 */
export const seedYdocFromTiptapContentJSON = (ydoc: Y.Doc, contentJSON: any): boolean => {
  try {
    if (!ydoc) return false;
    const fragment = ydoc.getXmlFragment('content');
    
    // 既存が空のときのみ挿入（冪等にしたい場合はクリア処理も検討）
    const hasExisting = (() => {
      try {
        const s = (fragment as any).toString?.() || '';
        return typeof s === 'string' && s.trim().length > 0;
      } catch {
        return false;
      }
    })();

    if (hasExisting) {
      return false;
    }

    const topLevelContent: any[] = Array.isArray(contentJSON?.content)
      ? contentJSON.content
      : (contentJSON?.type === 'doc' && Array.isArray(contentJSON.content) ? contentJSON.content : []);

    // トップレベルコンテンツ解析ログは削除

    const toXmlNodes = (nodes: any[]): (Y.XmlElement | Y.XmlText)[] => {
      const out: (Y.XmlElement | Y.XmlText)[] = [];
      for (const node of nodes || []) {
        if (!node || typeof node !== 'object') continue;
        const type = node.type;

        if (type === 'paragraph') {
          const el = new (Y as any).XmlElement('paragraph');
          if (Array.isArray(node.content)) {
            const childNodes = toXmlNodes(node.content);
            for (const c of childNodes) {
              try {
                el.insert(el.length, [c as any]);
              } catch {}
            }
          }
          out.push(el);
        } else if (type === 'heading') {
          const el = new (Y as any).XmlElement('heading');
          const level = node?.attrs?.level != null ? String(node.attrs.level) : '1';
          try {
            el.setAttribute('level', level);
          } catch {}
          if (Array.isArray(node.content)) {
            for (const child of node.content) {
              if (child && child.type === 'text') {
                const ytext = new (Y as any).XmlText();
                const txt = typeof child.text === 'string' ? child.text : '';
                if (txt) {
                  ytext.insert(0, txt);
                  el.insert(el.length, [ytext]);
                }
              }
            }
          }
          out.push(el);
        } else if (type === 'text') {
          const ytext = new (Y as any).XmlText();
          const txt = typeof node.text === 'string' ? node.text : '';
          if (txt) {
            ytext.insert(0, txt);
            out.push(ytext);
          }
        } else if (type === 'image' || type === 'resizableImage') {
          const el = new (Y as any).XmlElement('image');
          const a = node?.attrs || {};
          try {
            if (a.src != null) el.setAttribute('src', String(a.src));
            if (a.alt != null) el.setAttribute('alt', String(a.alt));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.width != null) el.setAttribute('width', String(a.width));
            if (a.height != null) el.setAttribute('height', String(a.height));
            if (a.style != null) el.setAttribute('style', String(a.style));
          } catch {}
          out.push(el);
        } else if (type === 'fileAttachment') {
          const el = new (Y as any).XmlElement('fileAttachment');
          const a = node?.attrs || {};
          try {
            if (a.fileId != null) el.setAttribute('fileId', String(a.fileId));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.mimeType != null) el.setAttribute('mimeType', String(a.mimeType));
            if (a.downloadURL != null) el.setAttribute('downloadURL', String(a.downloadURL));
            if (a.thumbnailURL != null) el.setAttribute('thumbnailURL', String(a.thumbnailURL));
          } catch {}
          out.push(el);
        } else if (TABLE_NODE_TYPES.includes(type)) {
          const el = buildTableXmlElement(node, toXmlNodes);
          if (el) out.push(el);
        } else if (Array.isArray(node.content)) {
          out.push(...toXmlNodes(node.content));
        } else {
          // 未対応ノードはスキップ
        }
      }
      return out;
    };

    const xmlNodes = toXmlNodes(topLevelContent);

    if (xmlNodes.length > 0) {
      fragment.insert(0, xmlNodes as any);
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * TipTap JSON内の画像/ファイル添付ノード数をカウント
 */
const countImageNodesInContent = (content: any[]): number => {
  let count = 0;
  const walk = (nodes: any[]) => {
    for (const n of nodes || []) {
      if (!n || typeof n !== 'object') continue;
      if (n.type === 'image' || n.type === 'resizableImage' || n.type === 'fileAttachment') count++;
      if (Array.isArray(n.content)) walk(n.content);
    }
  };
  walk(content);
  return count;
};

/**
 * Y.XmlFragment内の画像/ファイル添付ノード数をカウント
 */
const countImageNodesInYFragment = (fragment: any): number => {
  let count = 0;
  try {
    const len = typeof fragment?.length === 'number' ? fragment.length : 0;
    for (let i = 0; i < len; i++) {
      try {
        const child = fragment.get(i);
        if (!child) continue;
        const name = typeof child.nodeName === 'string' ? child.nodeName : '';
        if (name === 'image' || name === 'resizableImage' || name === 'fileAttachment') count++;
        if (typeof child.length === 'number') {
          for (let j = 0; j < child.length; j++) {
            try {
              const grand = child.get(j);
              const gname = typeof grand?.nodeName === 'string' ? grand.nodeName : '';
              if (gname === 'image' || gname === 'resizableImage' || gname === 'fileAttachment') count++;
            } catch {}
          }
        }
      } catch {}
    }
  } catch {}
  return count;
};

/**
 * previewData.thumbnailUrl または previewMeta.firstImageThumb から先頭に画像1枚を挿入する。
 * previewData.content に画像が無い場合のフォールバック（generatePreviewData が previewText で content を上書きするため）。
 */
export const prependImageFromPreviewThumbnailWhenMissing = (
  ydoc: Y.Doc,
  thumbnailUrl: string | null | undefined,
  firstImageThumb: string | null | undefined
): boolean => {
  try {
    if (!ydoc) return false;
    const src = thumbnailUrl || firstImageThumb;
    if (typeof src !== 'string' || !src.trim()) return false;
    const fragment = ydoc.getXmlFragment('content');
    const ydocImgCount = countImageNodesInYFragment(fragment);
    if (ydocImgCount > 0) return false;

    const imgEl = new (Y as any).XmlElement('image');
    try { imgEl.setAttribute('src', src.trim()); } catch {}
    const para = new (Y as any).XmlElement('paragraph');
    try { para.insert(0, [imgEl]); } catch {}

    Y.transact(ydoc, () => {
      (fragment as any).insert(0, [para]);
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * ydocBytesに画像が無いがpreviewDataに画像がある場合、previewData.contentでY.Docを置換して復元する。
 * ログ証拠: ydocImgCount=0 かつ previewImageCount>0 のときに呼び出す。
 */
export const replaceYdocContentFromPreviewDataWhenMissingImages = (
  ydoc: Y.Doc,
  previewContent: any[],
  _boardId?: string
): boolean => {
  try {
    if (!ydoc || !Array.isArray(previewContent) || previewContent.length === 0) return false;
    const fragment = ydoc.getXmlFragment('content');
    const ydocImgCount = countImageNodesInYFragment(fragment);
    const previewImgCount = countImageNodesInContent(previewContent);
    if (ydocImgCount > 0 || previewImgCount === 0) return false;

    const topLevelContent = previewContent;
    const toXmlNodes = (nodes: any[]): (Y.XmlElement | Y.XmlText)[] => {
      const out: (Y.XmlElement | Y.XmlText)[] = [];
      for (const node of nodes || []) {
        if (!node || typeof node !== 'object') continue;
        const type = node.type;
        if (type === 'paragraph') {
          const el = new (Y as any).XmlElement('paragraph');
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'heading') {
          const el = new (Y as any).XmlElement('heading');
          const level = node?.attrs?.level != null ? String(node.attrs.level) : '1';
          try { el.setAttribute('level', level); } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'text') {
          const ytext = new (Y as any).XmlText();
          const txt = typeof node.text === 'string' ? node.text : '';
          if (txt) { ytext.insert(0, txt); out.push(ytext); }
        } else if (type === 'image' || type === 'resizableImage') {
          const el = new (Y as any).XmlElement('image');
          const a = node?.attrs || {};
          try {
            if (a.src != null) el.setAttribute('src', String(a.src));
            if (a.alt != null) el.setAttribute('alt', String(a.alt));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.width != null) el.setAttribute('width', String(a.width));
            if (a.height != null) el.setAttribute('height', String(a.height));
            if (a.style != null) el.setAttribute('style', String(a.style));
          } catch {}
          out.push(el);
        } else if (type === 'fileAttachment') {
          const el = new (Y as any).XmlElement('fileAttachment');
          const a = node?.attrs || {};
          try {
            if (a.fileId != null) el.setAttribute('fileId', String(a.fileId));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.mimeType != null) el.setAttribute('mimeType', String(a.mimeType));
            if (a.downloadURL != null) el.setAttribute('downloadURL', String(a.downloadURL));
            if (a.thumbnailURL != null) el.setAttribute('thumbnailURL', String(a.thumbnailURL));
          } catch {}
          out.push(el);
        } else if (TABLE_NODE_TYPES.includes(type)) {
          const el = buildTableXmlElement(node, toXmlNodes);
          if (el) out.push(el);
        } else if (Array.isArray(node.content)) {
          out.push(...toXmlNodes(node.content));
        }
      }
      return out;
    };
    const xmlNodes = toXmlNodes(topLevelContent);
    if (xmlNodes.length === 0) return false;

    Y.transact(ydoc, () => {
      const len = typeof (fragment as any).length === 'number' ? (fragment as any).length : 0;
      if (len > 0) (fragment as any).delete(0, len);
      (fragment as any).insert(0, xmlNodes as any);
    });
    return true;
  } catch {
    return false;
  }
};

const CLICKABLE_LINK_CLASS = 'text-blue-600 underline cursor-pointer';

/**
 * ydoc の content フラグメント内の link ノードで、class に pointer-events-none が含まれるものを
 * クリック可能な class に書き換える。復元後にどの経路を使っても確実にリンクを有効にするために使用。
 * nodeName が 'link' でない場合でも、href 属性を持つ要素を対象にする。
 */
/**
 * Y.XmlText の delta 内の link 属性で pointer-events-none をクリック可能な class に置換する。
 * y-prosemirror は link マークを Y.XmlText の format 属性（delta.attributes.link または link--hash）で保存する。
 */
const normalizeLinkClassesInXmlText = (ytext: any): void => {
  try {
    if (!ytext || typeof ytext.toDelta !== 'function') return;
    const deltas = ytext.toDelta();
    let pos = 0;
    for (const d of deltas || []) {
      const insert = d.insert;
      const attrs = d.attributes;
      const len = typeof insert === 'string' ? insert.length : 0;
      if (len > 0 && attrs && typeof attrs === 'object') {
        const linkKey = Object.keys(attrs).find((k) => k === 'link' || k.startsWith('link--'));
        if (linkKey) {
          const linkAttrs = attrs[linkKey];
          if (linkAttrs && typeof linkAttrs === 'object') {
            const cls = linkAttrs.class;
            if (cls != null && String(cls).includes('pointer-events-none')) {
              const newLinkAttrs = { ...linkAttrs, class: CLICKABLE_LINK_CLASS };
              ytext.format(pos, len, { [linkKey]: newLinkAttrs });
            }
          }
        }
      }
      pos += len;
    }
  } catch (_) {}
};

export const normalizeLinkClassesInYdoc = (ydoc: Y.Doc): void => {
  try {
    if (!ydoc) return;
    const frag = ydoc.getXmlFragment('content');
    if (!frag) return;
    const walk = (node: any): void => {
      if (!node) return;
      try {
        if (node instanceof Y.XmlText) {
          normalizeLinkClassesInXmlText(node);
          return;
        }
        const name = typeof node.nodeName === 'string' ? node.nodeName : '';
        const href = node.getAttribute?.('href');
        const cls = node.getAttribute?.('class');
        const isLink = name === 'link' || (href != null && href !== '');
        if (isLink && cls != null && String(cls).includes('pointer-events-none')) {
          node.setAttribute('class', CLICKABLE_LINK_CLASS);
        }
        let child = node.firstChild;
        while (child) {
          walk(child);
          child = child.nextSibling;
        }
      } catch (_) {}
    };
    Y.transact(ydoc, () => {
      let child = (frag as any).firstChild;
      while (child) {
        try {
          walk(child);
        } catch (_) {}
        child = child.nextSibling;
      }
    });
  } catch (_) {}
};

/**
 * 指定した TipTap contentJSON で Y.Doc の content フラグメントを完全に置き換える。
 * 編集履歴の「復元」で、Y.applyUpdate のマージではなく確実に差し替えるために使用する。
 */
export const copyContentFragmentBetweenDocs = (sourceDoc: Y.Doc, targetDoc: Y.Doc): boolean => {
  try {
    if (!sourceDoc || !targetDoc) return false;
    const srcFrag = sourceDoc.getXmlFragment('content');
    const tgtFrag = targetDoc.getXmlFragment('content');
    const srcFragLength = typeof srcFrag?.length === 'number' ? srcFrag.length : 0;
    if (!srcFrag || srcFrag.length === 0) {
      Y.transact(targetDoc, () => {
        const len = typeof (tgtFrag as any).length === 'number' ? (tgtFrag as any).length : 0;
        if (len > 0) (tgtFrag as any).delete(0, len);
      });
      return true;
    }
    const IMAGE_ATTRS = ['src', 'alt', 'title', 'width', 'height', 'style'];
    const HEADING_ATTRS = ['level'];
    const FILE_ATTRS = ['fileId', 'title', 'mimeType', 'downloadURL', 'thumbnailURL'];
    const cloneInto = (srcNode: any, targetYdoc: Y.Doc): Y.XmlElement | Y.XmlText | null => {
      if (!srcNode) return null;
      const nodeName = typeof srcNode.nodeName === 'string' ? srcNode.nodeName : '';
      if (nodeName === 'paragraph' || nodeName === 'heading' || nodeName === 'image' || nodeName === 'resizableImage' || nodeName === 'fileAttachment') {
        const el = new (Y as any).XmlElement(nodeName === 'resizableImage' ? 'image' : nodeName);
        const attrsToTry = nodeName === 'image' || nodeName === 'resizableImage' ? IMAGE_ATTRS : nodeName === 'heading' ? HEADING_ATTRS : nodeName === 'fileAttachment' ? FILE_ATTRS : [];
        for (const key of attrsToTry) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) el.setAttribute(key, String(v));
          } catch (_) {}
        }
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'hashtag') {
        const el = new (Y as any).XmlElement('hashtag');
        const tag = srcNode.getAttribute?.('tag');
        if (tag != null) el.setAttribute('tag', String(tag));
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'autoLink') {
        const el = new (Y as any).XmlElement('autoLink');
        const linkText = srcNode.getAttribute?.('linkText');
        if (linkText != null) el.setAttribute('linkText', String(linkText));
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'videoEmbed') {
        const el = new (Y as any).XmlElement('videoEmbed');
        const attrs = ['src', 'width', 'height'];
        for (const key of attrs) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) el.setAttribute(key, String(v));
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'thumbnail') {
        const el = new (Y as any).XmlElement('thumbnail');
        const attrs = ['pageTitle', 'linkText', 'position'];
        for (const key of attrs) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) el.setAttribute(key, String(v));
          } catch (_) {}
        }
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'link') {
        const el = new (Y as any).XmlElement('link');
        const linkAttrKeys = ['href', 'target', 'rel', 'class'];
        for (const key of linkAttrKeys) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) {
              if (key === 'class' && String(v).includes('pointer-events-none')) {
                el.setAttribute('class', 'text-blue-600 underline cursor-pointer');
              } else {
                el.setAttribute(key, String(v));
              }
            }
          } catch (_) {}
        }
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName === 'inlineImage') {
        const el = new (Y as any).XmlElement('inlineImage');
        for (const key of ['src', 'alt', 'title']) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) el.setAttribute(key, String(v));
          } catch (_) {}
        }
        return el;
      }
      if (TABLE_NODE_TYPES.includes(nodeName)) {
        const el = new (Y as any).XmlElement(nodeName);
        // colspan / rowspan / colwidth は prosemirror-tables が数値・配列として扱うため
        // String() を通さず元の型のまま複製する
        if (nodeName === 'tableCell' || nodeName === 'tableHeader') {
          for (const key of ['colspan', 'rowspan', 'colwidth']) {
            try {
              const v = srcNode.getAttribute?.(key);
              if (v != null) el.setAttribute(key, v);
            } catch (_) {}
          }
        }
        const len = typeof srcNode.length === 'number' ? srcNode.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (nodeName && typeof srcNode.length === 'number') {
        const el = new (Y as any).XmlElement(nodeName);
        for (const key of [...IMAGE_ATTRS, ...HEADING_ATTRS, ...FILE_ATTRS, 'tag', 'linkText', 'src', 'width', 'height', 'pageTitle', 'position']) {
          try {
            const v = srcNode.getAttribute?.(key);
            if (v != null) el.setAttribute(key, String(v));
          } catch (_) {}
        }
        for (let i = 0; i < srcNode.length; i++) {
          try {
            const child = cloneInto(srcNode.get(i), targetYdoc);
            if (child) el.insert(el.length, [child as any]);
          } catch (_) {}
        }
        return el;
      }
      if (srcNode instanceof Y.XmlText || (typeof srcNode.toString === 'function' && (nodeName === '' || nodeName == null))) {
        const ytext = new (Y as any).XmlText();
        try {
          const delta = srcNode.toDelta?.();
          if (delta && Array.isArray(delta) && delta.length > 0) {
            ytext.applyDelta(delta);
          } else {
            const txt = srcNode.toString?.() ?? '';
            if (txt) ytext.insert(0, txt);
          }
        } catch (_) {
          const txt = srcNode.toString?.() ?? '';
          if (txt) ytext.insert(0, txt);
        }
        return ytext;
      }
      return null;
    };
    const nodes: (Y.XmlElement | Y.XmlText)[] = [];
    for (let i = 0; i < srcFrag.length; i++) {
      try {
        const n = cloneInto(srcFrag.get(i), targetDoc);
        if (n) nodes.push(n);
      } catch (_) {}
    }
    Y.transact(targetDoc, () => {
      const len = typeof (tgtFrag as any).length === 'number' ? (tgtFrag as any).length : 0;
      if (len > 0) (tgtFrag as any).delete(0, len);
      if (nodes.length > 0) (tgtFrag as any).insert(0, nodes as any);
    });
    return true;
  } catch {
    return false;
  }
};

export const replaceYdocContentWithContentJSON = (ydoc: Y.Doc, contentJSON: any): boolean => {
  try {
    if (!ydoc) return false;
    const topLevelContent: any[] =
      contentJSON?.type === 'doc' && Array.isArray(contentJSON.content)
        ? contentJSON.content
        : Array.isArray(contentJSON?.content)
          ? contentJSON.content
          : Array.isArray(contentJSON)
            ? contentJSON
            : [];
    if (topLevelContent.length === 0) {
      const fragment = ydoc.getXmlFragment('content');
      const len = typeof (fragment as any).length === 'number' ? (fragment as any).length : 0;
      if (len > 0) Y.transact(ydoc, () => (fragment as any).delete(0, len));
      return true;
    }
    const fragment = ydoc.getXmlFragment('content');
    const toXmlNodes = (nodes: any[]): (Y.XmlElement | Y.XmlText)[] => {
      const out: (Y.XmlElement | Y.XmlText)[] = [];
      for (const node of nodes || []) {
        if (!node || typeof node !== 'object') continue;
        const type = node.type;
        if (type === 'paragraph') {
          const el = new (Y as any).XmlElement('paragraph');
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'heading') {
          const el = new (Y as any).XmlElement('heading');
          const level = node?.attrs?.level != null ? String(node.attrs.level) : '1';
          try { el.setAttribute('level', level); } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'text') {
          const linkMark = Array.isArray(node.marks) ? node.marks.find((m: any) => m?.type === 'link') : null;
          const linkAttrs = linkMark?.attrs;
          const txt = typeof node.text === 'string' ? node.text : '';
          if (linkAttrs?.href && txt) {
            const linkEl = new (Y as any).XmlElement('link');
            try {
              if (linkAttrs.href != null) linkEl.setAttribute('href', String(linkAttrs.href));
              if (linkAttrs.target != null) linkEl.setAttribute('target', String(linkAttrs.target));
              if (linkAttrs.rel != null) linkEl.setAttribute('rel', String(linkAttrs.rel));
              const cls = linkAttrs.class != null ? String(linkAttrs.class) : '';
              if (cls && !cls.includes('pointer-events-none')) {
                linkEl.setAttribute('class', cls);
              } else {
                linkEl.setAttribute('class', 'text-blue-600 underline cursor-pointer');
              }
            } catch {}
            const ytext = new (Y as any).XmlText();
            ytext.insert(0, txt);
            linkEl.insert(0, [ytext as any]);
            out.push(linkEl);
          } else if (txt) {
            const ytext = new (Y as any).XmlText();
            ytext.insert(0, txt);
            out.push(ytext);
          }
        } else if (type === 'link') {
          const el = new (Y as any).XmlElement('link');
          const a = node?.attrs || {};
          try {
            if (a.href != null) el.setAttribute('href', String(a.href));
            if (a.target != null) el.setAttribute('target', String(a.target));
            if (a.rel != null) el.setAttribute('rel', String(a.rel));
            const cls = a.class != null ? String(a.class) : '';
            if (cls && !cls.includes('pointer-events-none')) {
              el.setAttribute('class', cls);
            } else {
              el.setAttribute('class', 'text-blue-600 underline cursor-pointer');
            }
          } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'autoLink') {
          const el = new (Y as any).XmlElement('autoLink');
          const a = node?.attrs || {};
          try {
            if (a.linkText != null) el.setAttribute('linkText', String(a.linkText));
          } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'image' || type === 'resizableImage') {
          const el = new (Y as any).XmlElement('image');
          const a = node?.attrs || {};
          try {
            if (a.src != null) el.setAttribute('src', String(a.src));
            if (a.alt != null) el.setAttribute('alt', String(a.alt));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.width != null) el.setAttribute('width', String(a.width));
            if (a.height != null) el.setAttribute('height', String(a.height));
            if (a.style != null) el.setAttribute('style', String(a.style));
          } catch {}
          out.push(el);
        } else if (type === 'fileAttachment') {
          const el = new (Y as any).XmlElement('fileAttachment');
          const a = node?.attrs || {};
          try {
            if (a.fileId != null) el.setAttribute('fileId', String(a.fileId));
            if (a.title != null) el.setAttribute('title', String(a.title));
            if (a.mimeType != null) el.setAttribute('mimeType', String(a.mimeType));
            if (a.downloadURL != null) el.setAttribute('downloadURL', String(a.downloadURL));
            if (a.thumbnailURL != null) el.setAttribute('thumbnailURL', String(a.thumbnailURL));
          } catch {}
          out.push(el);
        } else if (type === 'hashtag') {
          const el = new (Y as any).XmlElement('hashtag');
          const a = node?.attrs || {};
          try {
            if (a.tag != null) el.setAttribute('tag', String(a.tag));
          } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'videoEmbed') {
          const el = new (Y as any).XmlElement('videoEmbed');
          const a = node?.attrs || {};
          try {
            if (a.src != null) el.setAttribute('src', String(a.src));
            if (a.width != null) el.setAttribute('width', String(a.width));
            if (a.height != null) el.setAttribute('height', String(a.height));
          } catch {}
          out.push(el);
        } else if (type === 'thumbnail') {
          const el = new (Y as any).XmlElement('thumbnail');
          const a = node?.attrs || {};
          try {
            if (a.pageTitle != null) el.setAttribute('pageTitle', String(a.pageTitle));
            if (a.linkText != null) el.setAttribute('linkText', String(a.linkText));
            if (a.position != null) el.setAttribute('position', String(a.position));
          } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'hardBreak') {
          const ytext = new (Y as any).XmlText();
          ytext.insert(0, '\n');
          out.push(ytext);
        } else if (type === 'bulletList' || type === 'orderedList' || type === 'taskList') {
          const el = new (Y as any).XmlElement(type);
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (type === 'listItem' || type === 'taskItem') {
          const el = new (Y as any).XmlElement(type);
          const a = node?.attrs || {};
          try {
            if (a.checked != null) el.setAttribute('checked', String(a.checked));
          } catch {}
          if (Array.isArray(node.content)) {
            for (const c of toXmlNodes(node.content)) {
              try { el.insert(el.length, [c as any]); } catch {}
            }
          }
          out.push(el);
        } else if (TABLE_NODE_TYPES.includes(type)) {
          const el = buildTableXmlElement(node, toXmlNodes);
          if (el) out.push(el);
        } else if (Array.isArray(node.content)) {
          out.push(...toXmlNodes(node.content));
        }
      }
      return out;
    };
    const xmlNodes = toXmlNodes(topLevelContent);
    Y.transact(ydoc, () => {
      const len = typeof (fragment as any).length === 'number' ? (fragment as any).length : 0;
      if (len > 0) (fragment as any).delete(0, len);
      if (xmlNodes.length > 0) (fragment as any).insert(0, xmlNodes as any);
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Yjsデータの構造チェック（事前検証）
 */
const isValidYjsData = (data: Uint8Array): boolean => {
  try {
    // 基本的な長さチェック
    if (!data || data.length === 0) {
      return false;
    }

    // 最小サイズチェック（Yjsの最小有効データサイズ）
    if (data.length < 4) {
      return false;
    }

    // 🔧 firstByte チェックを削除
    // Y.encodeStateAsUpdateV2 は正常なデータでも firstByte: 0 になることがあります
    // （Y.js の仕様）

    // データの一貫性チェック（nullバイトの異常な集中）
    // より厳しい基準に変更（95%以上が null の場合のみエラー）
    const nullCount = Array.from(data).filter(byte => byte === 0).length;
    const nullRatio = nullCount / data.length;
    if (nullRatio > 0.95) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * 復元後のY.Docの整合性チェック
 */
const validateRestoredYdoc = (ydoc: Y.Doc): boolean => {
  try {
    // Y.Docの基本構造チェック
    if (!ydoc || !ydoc.share) {
      return false;
    }

    // shareの型チェック
    if (!(ydoc.share instanceof Map)) {
      return false;
    }

    // 異常に多いフィールド数のチェック（データ破損の可能性）
    const fieldCount = ydoc.share.size;
    if (fieldCount > 1000) {
      return false;
    }

    // 各フィールドの基本整合性チェック
    let invalidFieldCount = 0;
    ydoc.share.forEach((field, key) => {
      try {
        if (!field || typeof field !== 'object') {
          invalidFieldCount++;
          return;
        }

        // Y.AbstractTypeの基本チェック
        if (field instanceof Y.AbstractType) {
          // 正常なY.AbstractType
        } else {
          // 非Y.AbstractTypeオブジェクトのチェック
          if (typeof (field as any).toString !== 'function') {
            invalidFieldCount++;
          }
        }
      } catch (error) {
        invalidFieldCount++;
      }
    });

    // 無効フィールドの割合チェック
    const invalidRatio = invalidFieldCount / Math.max(fieldCount, 1);
    if (invalidRatio > 0.5) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Base64文字列をYjsのUint8Arrayにデコード
 */
export const decodeBase64ToYjs = (base64String: string): Uint8Array => {
  try {
    // Base64文字列をバイナリに変換
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes;
  } catch {
    return new Uint8Array();
  }
};

/**
 * 型点検ユーティリティ（開発時のみ実行推奨）
 */
export function debugLogYdocShare(_ydoc: Y.Doc) {
  // ログが多くなるため、現在は何もしないダミー実装にしておく
}
